import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronRight, RotateCcw, Trophy, BookOpen, MessageSquare, Shuffle } from 'lucide-react';
import { useT } from '../i18n';

const renderCloze = (text, revealed = false) => {
  if (!text) return null;
  const parts = text.split(/\{([^}]+)\}/g);
  return parts.map((part, i) => {
    if (i % 2 === 1) {
      return revealed
        ? <mark key={i} style={{ background: 'color-mix(in srgb, var(--success) 25%, transparent)', color: 'var(--success)', padding: '1px 5px', borderRadius: '5px', fontWeight: 800 }}>{part}</mark>
        : <span key={i} style={{ display: 'inline-block', background: 'var(--border)', borderRadius: '5px', padding: '1px 10px', letterSpacing: '0.12em', color: 'transparent', userSelect: 'none' }}>{'_'.repeat(Math.max(3, part.length))}</span>;
    }
    return <span key={i}>{part}</span>;
  });
};

const btn = (style = {}) => ({
  border: 'none',
  borderRadius: '12px',
  cursor: 'pointer',
  fontWeight: 700,
  fontSize: '14px',
  padding: '10px 20px',
  transition: 'opacity 0.15s, transform 0.1s',
  ...style,
});

export function QuizModal({ isOpen, onClose, nodes, useQuizSession: quizHook }) {
  const t = useT();
  const {
    phase, currentQuestion, currentIndex, questions, generationProgress,
    error, lastSession, isLastQuestion, reviewCount,
    startQuiz, answerQuestion, nextQuestion, finishQuiz, reset,
  } = quizHook;

  const QUESTION_TYPES = [
    { id: 'closed', label: t('quiz.typeClosed'), icon: <BookOpen size={15} />, desc: t('quiz.typeClosedDesc') },
    { id: 'open',   label: t('quiz.typeOpen'),   icon: <MessageSquare size={15} />, desc: t('quiz.typeOpenDesc') },
    { id: 'mixed',  label: t('quiz.typeMixed'),  icon: <Shuffle size={15} />, desc: t('quiz.typeMixedDesc') },
  ];

  const [config, setConfig] = useState({ count: 10, type: 'mixed' });
  const [questionPhase, setQuestionPhase] = useState('answering');
  const [textAnswer, setTextAnswer] = useState('');
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen, reset]);

  useEffect(() => {
    if (phase === 'session') {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setQuestionPhase('answering');
      setTextAnswer('');
    }
  }, [currentIndex, phase]);

  useEffect(() => {
    if (questionPhase === 'answering' && currentQuestion?.type === 'open') {
      textareaRef.current?.focus();
    }
  }, [questionPhase, currentQuestion]);

  if (!isOpen) return null;

  const handleReveal = () => setQuestionPhase('evaluating');

  const handleConfirmOpen = () => {
    if (!textAnswer.trim()) return;
    setQuestionPhase('evaluating');
  };

  const handleEval = (isCorrect) => {
    answerQuestion(isCorrect, textAnswer);
    if (isLastQuestion) {
      finishQuiz(config);
    } else {
      nextQuestion();
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const progress = questions.length > 0
    ? Math.round(((currentIndex + (questionPhase === 'evaluating' ? 1 : 0)) / questions.length) * 100)
    : 0;

  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div
        style={{
          width: '100%', maxWidth: '520px',
          background: 'var(--bg-dim)',
          border: '1px solid var(--border)',
          borderRadius: '20px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
          overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px 14px' }}>
          <span style={{ fontSize: '16px', fontWeight: 800, color: 'var(--text-primary)' }}>
            {phase === 'results' ? t('quiz.results') : t('quiz.title')}
          </span>
          <button onClick={handleClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: '4px' }}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '0 20px 20px', minHeight: '320px', display: 'flex', flexDirection: 'column' }}>

          {/* ── CONFIG ── */}
          {phase === 'idle' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {reviewCount > 0 && (
                <div style={{
                  background: 'color-mix(in srgb, var(--warning) 12%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--warning) 30%, transparent)',
                  borderRadius: '12px', padding: '10px 14px',
                  fontSize: '13px', color: 'var(--warning)', fontWeight: 600,
                }}>
                  {t(reviewCount === 1 ? 'quiz.reviewWarning_one' : 'quiz.reviewWarning_other', { count: reviewCount })}
                </div>
              )}

              <div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '10px' }}>
                  {t('quiz.questionCount')}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <input
                    type="range" min={3} max={Math.min(20, nodes.length)}
                    value={config.count}
                    onChange={e => setConfig(c => ({ ...c, count: +e.target.value }))}
                    style={{ flex: 1, accentColor: 'var(--accent-2)' }}
                  />
                  <span style={{ fontSize: '22px', fontWeight: 800, color: 'var(--accent-2)', minWidth: '32px', textAlign: 'right' }}>
                    {config.count}
                  </span>
                </div>
              </div>

              <div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '10px' }}>
                  {t('quiz.questionType')}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {QUESTION_TYPES.map(qt => (
                    <button
                      key={qt.id}
                      onClick={() => setConfig(c => ({ ...c, type: qt.id }))}
                      style={{
                        flex: 1, padding: '10px 8px',
                        borderRadius: '12px',
                        border: config.type === qt.id
                          ? '2px solid var(--accent-2)'
                          : '2px solid var(--border-s)',
                        background: config.type === qt.id
                          ? 'color-mix(in srgb, var(--accent-2) 12%, transparent)'
                          : 'var(--surface)',
                        color: config.type === qt.id ? 'var(--accent-2)' : 'var(--text-secondary)',
                        cursor: 'pointer',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
                        transition: 'all 0.15s',
                      }}
                    >
                      {qt.icon}
                      <span style={{ fontSize: '13px', fontWeight: 700 }}>{qt.label}</span>
                      <span style={{ fontSize: '11px', fontWeight: 400, opacity: 0.7 }}>{qt.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {error && (
                <div style={{ fontSize: '13px', color: 'var(--danger)', background: 'color-mix(in srgb, var(--danger) 10%, transparent)', borderRadius: '10px', padding: '10px 14px' }}>
                  {error}
                </div>
              )}

              <button
                onClick={() => startQuiz(config)}
                disabled={nodes.length === 0}
                style={btn({ background: 'var(--accent-2)', color: '#fff', marginTop: '4px', opacity: nodes.length === 0 ? 0.4 : 1 })}
              >
                {t('quiz.start')}
              </button>
            </div>
          )}

          {/* ── GENERATING ── */}
          {phase === 'generating' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px' }}>
              <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)' }}>
                {t('quiz.generating')}
              </div>
              <div style={{ width: '100%', height: '6px', background: 'var(--surface)', borderRadius: '99px', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: '99px',
                  background: 'var(--accent-2)',
                  width: `${generationProgress}%`,
                  transition: 'width 0.3s ease',
                }} />
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{generationProgress}%</div>
            </div>
          )}

          {/* ── SESSION ── */}
          {phase === 'session' && currentQuestion && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Progress */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ flex: 1, height: '4px', background: 'var(--surface)', borderRadius: '99px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', borderRadius: '99px', background: 'var(--accent-2)', width: `${progress}%`, transition: 'width 0.3s' }} />
                </div>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 600, flexShrink: 0 }}>
                  {currentIndex + 1} / {questions.length}
                </span>
              </div>

              {/* Node label */}
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {currentQuestion.type === 'closed' ? t('quiz.labelClosed') : t('quiz.labelOpen')} · {currentQuestion.nodeTitle}
              </div>

              {/* Question */}
              <div style={{
                background: 'var(--surface)',
                borderRadius: '14px',
                padding: '18px',
                fontSize: '16px',
                fontWeight: 700,
                color: 'var(--text-primary)',
                lineHeight: 1.6,
              }}>
                {currentQuestion.type === 'closed'
                  ? renderCloze(currentQuestion.front, questionPhase === 'evaluating')
                  : currentQuestion.front}
              </div>

              {/* CLOSED — answering */}
              {currentQuestion.type === 'closed' && questionPhase === 'answering' && (
                <button onClick={handleReveal} style={btn({ background: 'var(--surface)', color: 'var(--text-primary)', border: '2px solid var(--border-s)' })}>
                  {t('quiz.showAnswer')}
                </button>
              )}

              {/* CLOSED — evaluating */}
              {currentQuestion.type === 'closed' && questionPhase === 'evaluating' && (
                <>
                  <div style={{
                    background: 'color-mix(in srgb, var(--success) 10%, transparent)',
                    border: '1px solid color-mix(in srgb, var(--success) 30%, transparent)',
                    borderRadius: '14px', padding: '14px 18px',
                    fontSize: '15px', color: 'var(--text-primary)', lineHeight: 1.5,
                  }}>
                    {currentQuestion.back}
                  </div>
                  <EvalButtons t={t} onKnown={() => handleEval(true)} onReview={() => handleEval(false)} />
                </>
              )}

              {/* OPEN — answering */}
              {currentQuestion.type === 'open' && questionPhase === 'answering' && (
                <>
                  <textarea
                    ref={textareaRef}
                    value={textAnswer}
                    onChange={e => setTextAnswer(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleConfirmOpen(); }}
                    placeholder={t('quiz.answerPlaceholder')}
                    rows={4}
                    style={{
                      width: '100%', resize: 'none',
                      background: 'var(--surface)',
                      border: '2px solid var(--border-s)',
                      borderRadius: '12px', padding: '12px 14px',
                      fontSize: '14px', color: 'var(--text-primary)',
                      outline: 'none', fontFamily: 'inherit',
                      boxSizing: 'border-box',
                      transition: 'border-color 0.2s',
                    }}
                    onFocus={e => { e.currentTarget.style.borderColor = 'var(--accent-2)'; }}
                    onBlur={e => { e.currentTarget.style.borderColor = 'var(--border-s)'; }}
                  />
                  <button
                    onClick={handleConfirmOpen}
                    disabled={!textAnswer.trim()}
                    style={btn({ background: 'var(--accent-2)', color: '#fff', opacity: textAnswer.trim() ? 1 : 0.4, display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' })}
                  >
                    {t('quiz.compare')} <ChevronRight size={15} />
                  </button>
                </>
              )}

              {/* OPEN — evaluating */}
              {currentQuestion.type === 'open' && questionPhase === 'evaluating' && (
                <>
                  {textAnswer.trim() && (
                    <div style={{
                      background: 'var(--surface)', borderRadius: '12px', padding: '12px 14px',
                      fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.5,
                    }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                        {t('quiz.yourAnswer')}
                      </div>
                      {textAnswer}
                    </div>
                  )}
                  <div style={{
                    background: 'color-mix(in srgb, var(--success) 10%, transparent)',
                    border: '1px solid color-mix(in srgb, var(--success) 30%, transparent)',
                    borderRadius: '14px', padding: '14px 18px',
                    fontSize: '14px', color: 'var(--text-primary)', lineHeight: 1.5,
                  }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--success)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                      {t('quiz.modelAnswer')}
                    </div>
                    {currentQuestion.back}
                  </div>
                  <EvalButtons t={t} onKnown={() => handleEval(true)} onReview={() => handleEval(false)} />
                </>
              )}
            </div>
          )}

          {/* ── RESULTS ── */}
          {phase === 'results' && lastSession && (
            <ResultsView t={t} session={lastSession} onRetry={() => reset()} onClose={handleClose} />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

function EvalButtons({ t, onKnown, onReview }) {
  return (
    <div style={{ display: 'flex', gap: '10px' }}>
      <button
        onClick={onReview}
        style={btn({
          flex: 1,
          background: 'color-mix(in srgb, var(--danger) 12%, transparent)',
          border: '2px solid color-mix(in srgb, var(--danger) 30%, transparent)',
          color: 'var(--danger)',
        })}
      >
        {t('quiz.review')}
      </button>
      <button
        onClick={onKnown}
        style={btn({
          flex: 1,
          background: 'color-mix(in srgb, var(--success) 12%, transparent)',
          border: '2px solid color-mix(in srgb, var(--success) 30%, transparent)',
          color: 'var(--success)',
        })}
      >
        {t('quiz.known')}
      </button>
    </div>
  );
}

function ResultsView({ t, session, onRetry, onClose }) {
  const { correct, total } = session.score;
  const pct = Math.round((correct / total) * 100);
  const wrong = session.questions.filter(q => !q.isCorrect);

  const emoji = pct === 100 ? '🏆' : pct >= 70 ? '✅' : pct >= 40 ? '📚' : '💪';
  const labelKey = pct === 100 ? 'quiz.scoreLabel_perfect' : pct >= 70 ? 'quiz.scoreLabel_good' : pct >= 40 ? 'quiz.scoreLabel_ok' : 'quiz.scoreLabel_low';
  const label = t(labelKey);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Score */}
      <div style={{ textAlign: 'center', padding: '12px 0' }}>
        <div style={{ fontSize: '48px', marginBottom: '8px' }}>{emoji}</div>
        <div style={{ fontSize: '32px', fontWeight: 900, color: 'var(--accent-2)' }}>
          {correct}<span style={{ fontSize: '18px', color: 'var(--text-muted)', fontWeight: 600 }}>/{total}</span>
        </div>
        <div style={{ fontSize: '14px', color: 'var(--text-secondary)', fontWeight: 600, marginTop: '4px' }}>{label}</div>
      </div>

      {/* Wrong answers */}
      {wrong.length > 0 && (
        <div>
          <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>
            {t('quiz.wrongSection', { count: wrong.length })}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '200px', overflowY: 'auto' }}>
            {wrong.map((q, i) => (
              <div key={i} style={{
                background: 'var(--surface)', borderRadius: '10px', padding: '10px 14px',
                fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.4,
              }}>
                <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>{q.front}</div>
                <div style={{ color: 'var(--text-muted)' }}>{q.back}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
        <button
          onClick={onRetry}
          style={btn({
            flex: 1,
            background: 'var(--surface)',
            border: '2px solid var(--border-s)',
            color: 'var(--text-primary)',
            display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center',
          })}
        >
          <RotateCcw size={14} /> {t('quiz.newQuiz')}
        </button>
        <button
          onClick={onClose}
          style={btn({ flex: 1, background: 'var(--accent-2)', color: '#fff', display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' })}
        >
          <Trophy size={14} /> {t('quiz.close')}
        </button>
      </div>
    </div>
  );
}
